import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableRemotePagination } from 'components/@shared/TableRemotePagination';
import { PATH } from 'routes/path';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { OrderValue, SortOrder, TableParams } from 'types/common';
import { useDidMount } from 'hooks/useDidMount';
import { State, initialHomePagesTableParams, initialState, tableColumns } from './constants';
import { BackdropLoader } from 'components/@shared/Loaders';
import TextField from '@mui/material/TextField';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { mainHeaderHeight } from 'theme/constants';
import { NoListYet } from 'components/@shared/NoListYet';
import { NoResultsFound } from 'components/@shared/NoResultsFound';
import { useModal } from 'hooks/useModal';
import { WarningPopUp } from 'components/@shared/WarningPopUp';
import { parametrizeURL } from 'routes/utils';
import { deleteHomePage, getHomePages, setActiveHomePage } from 'services/homePageService';
import { HomePageResponseViewModel } from 'types/home-page';
import { InvoicesActionsMenu } from 'components/Invoices/List/ItemActionsMenu';
import { HomePageListRow } from 'components/Admin/List/Row';
import { LocalConvenienceStoreOutlined } from '@mui/icons-material';
import { ONLY_ADMIN } from 'constants/roles';
import { useDeniedAccess } from 'hooks/useDeniedAccess';
import { AdaptiveTable } from 'components/@shared/TableRemotePagination/AdaptiveTable';
import { ITableHeadCell } from 'components/@shared/TableRemotePagination/types';
import { HomePageListCell } from 'components/Admin/List/Row/HomePageListCell';
import { Headline } from 'components/@shared/Headline';

export const AdminSettingsHomePageListPage = () => {
    const [state, setState] = useState<State>(initialState);
    const [selectedItem, setSelectedItem] = useState('');
    const navigate = useNavigate();

    const removeModal = useModal();

    const loadList = useCallback(
        (param: Partial<TableParams>) => {
            const updatedParams: TableParams = { ...state.tableParams, ...param };
            getHomePages({ ...updatedParams, sortingOrder: Number(updatedParams.sortOrder === 'asc') as SortOrder })
                .then(({ items, count }) => {
                    setState((prevState) => ({
                        ...prevState,
                        isLoading: false,
                        list: items,
                        tableParams: updatedParams,
                        totalCount: count,
                    }));
                })
                .catch(() => {
                    setState((prevState) => ({ ...prevState, isLoading: false }));
                });
        },
        [state.tableParams],
    );

    const sortHandler = useCallback(
        (sortOrder: OrderValue, orderBy: string) => {
            loadList({ sortOrder, orderBy });
        },
        [loadList],
    );

    const setPageHandler = useCallback(
        (page: number) => {
            loadList({ page });
        },
        [loadList],
    );

    const setRowsPerPageHandler = useCallback(
        (pageSize: number) => {
            loadList({ page: 0, pageSize });
        },
        [loadList],
    );

    const searchChangeHandler = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        loadList({ page: 0, search: event.target.value });
    }, 500);

    const createNewHomePage = useCallback(() => navigate(PATH.CREATE_HOME_PAGE), [navigate]);

    const confirmDeleteHandler = useCallback(() => {
        removeModal.closeModal();
        setState((prevState) => ({ ...prevState, isLoading: true }));
        deleteHomePage(selectedItem)
            .then(() => loadList(initialHomePagesTableParams))
            .catch(() => {
                setState((prevState) => ({ ...prevState, isLoading: false }));
            });
    }, [state, removeModal, selectedItem]);

    const editHandler = useCallback(
        (id: string) => {
            navigate(parametrizeURL(PATH.EDIT_HOME_PAGE, { id }));
        },
        [navigate],
    );

    const handleSetAsActivePage = useCallback((id: string) => {
        setState((prevState) => ({ ...prevState, isLoading: false }));
        setActiveHomePage(id)
            .then(() => {
                setState((prevState) => ({
                    ...prevState,
                    isLoading: false,
                    list: prevState.list.map((item) => {
                        if (item.id !== id) {
                            return { ...item, isActive: false };
                        }
                        if (item.id === id) {
                            return { ...item, isActive: true };
                        }
                        return item;
                    }),
                }));
            })
            .catch(() => {
                setState((prevState) => ({ ...prevState, isLoading: false }));
            });
    }, []);

    const extraComponents = useMemo(
        () => ({
            Actions: ({ rowData }: { rowData: HomePageResponseViewModel }) => {
                const deleteHandler = (id: string) => {
                    setSelectedItem(id);
                    removeModal.openModal();
                };

                return <InvoicesActionsMenu id={rowData.id} onDelete={deleteHandler} onEdit={editHandler} />;
            },
            Row: ({ rowData }: { rowData: HomePageResponseViewModel }) => (
                <HomePageListRow {...{ rowData, columns: tableColumns }} onSetAsActive={handleSetAsActivePage} />
            ),
            Cell: ({ rowData, column }: { rowData: HomePageResponseViewModel; column: ITableHeadCell }) => (
                <HomePageListCell {...{ rowData, item: column }} onSetAsActive={handleSetAsActivePage} />
            ),
        }),
        [removeModal, editHandler],
    );

    useDidMount(() => {
        loadList(state.tableParams);
    });

    useDeniedAccess(ONLY_ADMIN);

    if (state.list.length === 0 && !!state.tableParams.search) {
        return (
            <Box height={`calc(100vh - ${mainHeaderHeight}px)`}>
                <NoResultsFound />
            </Box>
        );
    }

    if (state.list.length === 0 && !state.isLoading) {
        return (
            <Box height={`calc(100vh - ${mainHeaderHeight}px)`}>
                <NoListYet onClick={createNewHomePage} />
            </Box>
        );
    }

    return (
        <Box
            width="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            px={2.5}
            py={2}
        >
            <Headline title="Home page settings" />
            <Box>
                <Box display="flex" alignItems="center" gap={2} width="100%" mb={2}>
                    <Button onClick={createNewHomePage} variant="outlined">
                        Create new Home Page
                    </Button>
                    <TextField onChange={searchChangeHandler} size="small" label="Search" sx={{ ml: 'auto' }}>
                        Search
                    </TextField>
                </Box>
                <AdaptiveTable
                    rows={state.list}
                    columns={tableColumns}
                    tableParams={state.tableParams}
                    totalCount={state.totalCount}
                    components={extraComponents}
                    onSort={sortHandler}
                    onSetPage={setPageHandler}
                    onSetRowsPerPage={setRowsPerPageHandler}
                />
            </Box>
            {removeModal.open && (
                <WarningPopUp
                    isOpen={removeModal.open}
                    warningHeader="Remove Home page?"
                    warningBody={`Are you sure you want to remove Home page?`}
                    confirmButtonText="Remove"
                    onClose={removeModal.closeModal}
                    onConfirm={confirmDeleteHandler}
                />
            )}
            {state.isLoading && <BackdropLoader />}
        </Box>
    );
};
